'use client'

import { useEffect } from 'react'
import { DocsLayout } from 'fumadocs-ui/layouts/docs'
import { RootProvider } from 'fumadocs-ui/provider/next'
import { useRouter, usePathname } from 'next/navigation'
import type { ReactNode } from 'react'
import type * as PageTree from 'fumadocs-core/page-tree'
import { DOCS_CHROME, DOCS_LOCALE_NAMES, i18n } from '@/lib/docs-i18n'
import type { DocsLocale } from '@/lib/docs-i18n'
import { getDocsBasePath, setLocaleCookie } from '@/lib/docs-locale'
import { applyDocsTheme, clearDocsThemeClasses, resolveDocsTheme, type DocsTheme } from '@/lib/docs-theme'
import { DocsMobileSidebarBanner, DocsSidebarNavTitle } from '@/components/docs/DocsSidebarBrand'
import { DocsMobileSubNav } from '@/components/docs/DocsMobileSubNav'
import { DocsSidebarFooter } from '@/components/docs/DocsSidebarFooter'

// docs-theme.css loads from the shared app/docs/layout.tsx; theme initialization
// is injected into <head> by DocsThemeScript (no fumadocs-ui/style.css).
// Shared docs chrome (RootProvider + DocsLayout) for both the
// English ("/docs") and German ("/docs/de") route trees. Kept as one client
// component so the locale switch (`onChange`) can reconstruct the sibling
// URL client-side — the default locale-change behaviour assumes a
// `/<locale>/...` scheme (locale as the FIRST path segment), which does not
// match this app's scheme where the default locale ('en') is unprefixed and
// only 'de' adds a `/docs/de` segment. See lib/source.ts for the matching
// URL builder used server-side by the Fumadocs source loader.
//
// Hydration: warnings mentioning `data-cursor-ref` are Cursor embedded-browser
// only — not app code. Verify in external Chrome.
export function DocsChrome({
  tree,
  locale,
  initialTheme,
  children,
}: {
  tree: PageTree.Root
  locale: DocsLocale
  initialTheme: DocsTheme
  children: ReactNode
}) {
  const router = useRouter()
  const pathname = usePathname()
  const chrome = DOCS_CHROME[locale]
  const docsBase = getDocsBasePath(locale)

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  useEffect(() => {
    const toc = document.getElementById('nd-toc')
    toc?.setAttribute('role', 'navigation')
    toc?.setAttribute('aria-labelledby', 'toc-title')

    document.querySelectorAll<HTMLElement>('#nd-page [data-card] > h3').forEach((heading) => {
      heading.setAttribute('role', 'heading')
      heading.setAttribute('aria-level', '2')
    })
  }, [pathname])

  useEffect(() => {
    applyDocsTheme(resolveDocsTheme())
    return () => {
      clearDocsThemeClasses()
    }
  }, [])

  function onChange(nextLocale: string) {
    const docsLocale = nextLocale === 'de' ? 'de' : 'en'
    setLocaleCookie(docsLocale)
    const withoutDePrefix = pathname.replace(/^\/docs\/de(?=\/|$)/, '/docs')
    const rest = withoutDePrefix.slice('/docs'.length) // '' | '/foo/bar'
    const target = docsLocale === i18n.defaultLanguage ? `/docs${rest}` : `/docs/de${rest}`
    router.push(target)
    router.refresh()
  }

  const searchLinks: [string, string][] = [
    [chrome.searchInstall, `${docsBase}/install/hardware`],
    [chrome.searchIssues, `${docsBase}/operate/common-issues`],
    [chrome.searchConfiguration, `${docsBase}/configuration`],
  ]
  const locales = i18n.languages.map((code) => ({ locale: code, name: DOCS_LOCALE_NAMES[code] }))

  const themeOptions = {
    // next-themes renders an inline <script> that React 19 rejects in client
    // components. The root app/layout.tsx script initializes the theme instead.
    enabled: false as const,
  }
  const NavTitle = () => <DocsSidebarNavTitle locale={locale} />

  return (
    <RootProvider
      search={{ links: searchLinks, preload: false }}
      theme={themeOptions}
      i18n={{ locale, locales, onLocaleChange: onChange }}
    >
      <DocsLayout
        tree={tree}
        nav={{ component: <DocsMobileSubNav locale={locale} />, url: docsBase }}
        slots={{ navTitle: NavTitle }}
        i18n={false}
        themeSwitch={{ enabled: false }}
        sidebar={{
          banner: <DocsMobileSidebarBanner locale={locale} />,
          collapsible: false,
          footer: <DocsSidebarFooter locale={locale} initialTheme={initialTheme} onLocaleChange={onChange} />,
        }}
      >
        {children}
      </DocsLayout>
    </RootProvider>
  )
}
